import { PaperclipIcon } from "@phosphor-icons/react/dist/csr/Paperclip";
import { useRef } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { DocumentChip } from "./document-chip";
import { ACCEPTED_FILE_TYPES, MAX_DOCUMENT_BYTES } from "./document-constants";
import { useUploadReportDocuments } from "./query";
import type { ProfitabilityDocument, ReportInputSlot } from "./types";
import { reportError } from "./utils";

export const ReportSlotPicker = ({
  businessId,
  slot,
  document,
  onDocumentChange,
  showDescription = true,
  buttonLabel,
}: {
  businessId: string;
  slot: ReportInputSlot;
  document: ProfitabilityDocument | null;
  onDocumentChange: (document: ProfitabilityDocument | null) => void;
  showDescription?: boolean;
  buttonLabel?: string;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadReportDocuments(businessId);
  const handleFile = (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file) return;
    if (file.size > MAX_DOCUMENT_BYTES) {
      toast.error(`${file.name} is larger than 25 MB`);
      return;
    }
    upload.mutate([file], {
      onSuccess: ([uploaded]) => onDocumentChange(uploaded ?? null),
      onError: (error) =>
        toast.error(`${slot.title} could not be added`, {
          description: reportError(error),
        }),
    });
  };

  return (
    <Stack
      align="start"
      gap={0}
      className="min-w-0"
      data-testid={`slot-${slot.key}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES}
        className="hidden"
        data-testid={`slot-input-${slot.key}`}
        onChange={(event) => {
          handleFile(event.target.files);
          event.target.value = "";
        }}
      />
      {document ? (
        <DocumentChip
          document={document}
          onRemove={() => onDocumentChange(null)}
        />
      ) : (
        <Button
          disabled={upload.isPending}
          aria-label={
            buttonLabel ? `${buttonLabel} for ${slot.title}` : undefined
          }
          variant="secondary"
          aria-busy={upload.isPending}
          onClick={() => inputRef.current?.click()}
        >
          {<PaperclipIcon />}
          {buttonLabel ?? `Add ${slot.title}`}
        </Button>
      )}
      {showDescription && slot.description && !document && (
        <Text tone="muted" className="mt-1">
          {slot.description}
        </Text>
      )}
    </Stack>
  );
};
